import { useEffect, useState, useRef } from "react";
import api from "./api";
import { useLocale } from "../i18n/LocaleContext";

// Lightweight in-memory cache to dedupe /translate calls across the app.
const memoCache = new Map(); // key: `${lang}::${text}` -> translated

/**
 * Return an Italian source string translated on-the-fly to the current
 * site locale via the backend `/translate` endpoint (Claude Sonnet 4.6).
 *
 * - Italian locale → returns the source unchanged
 * - Empty / null text → returns as-is
 * - First call fetches from the server (which itself hits Mongo cache first,
 *   then LLM). Subsequent calls with the same text/lang return from memory.
 * - Errors fall back gracefully to the source so the UI never breaks.
 *
 * Use it like:  const translated = useTranslated(drop.story);
 */
export function useTranslated(text) {
  const { lang } = useLocale();
  const [out, setOut] = useState(text || "");
  const latest = useRef(0);

  useEffect(() => {
    latest.current += 1;
    const rid = latest.current;
    const src = text || "";
    if (!src || lang === "it") {
      setOut(src);
      return;
    }
    const key = `${lang}::${src}`;
    if (memoCache.has(key)) {
      setOut(memoCache.get(key));
      return;
    }
    // Show source while the translation is in flight so the UI isn't blank.
    setOut(src);
    api
      .post("/translate", { text: src, lang })
      .then((r) => {
        if (latest.current !== rid) return;
        const translated = r?.data?.text || src;
        memoCache.set(key, translated);
        setOut(translated);
      })
      .catch(() => {
        if (latest.current !== rid) setOut(src);
      });
  }, [text, lang]);

  return out;
}
